import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, AlertTriangle } from 'lucide-react';
import { api } from '@/core/apiClient';

interface Role {
  id: string;
  name: string;
}

interface Brand {
  id: string;
  name: string;
}

interface Company {
  id: number;
  name: string;
}

interface User {
  id: string;
  email: string;
  username?: string | null;
  firstName: string;
  lastName: string;
  phone?: string;
  roleId: string;
  brandId?: string;
  isAdminFullAccess: boolean;
  isActive: number;
  companyIds?: number[];
}

interface FormData {
  email: string;
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  roleId: string;
  brandId: string;
  isAdminFullAccess: boolean;
  isActive: boolean;
  companyIds: number[];
}

const initialFormData: FormData = {
  email: '',
  username: '',
  password: '',
  firstName: '',
  lastName: '',
  phone: '',
  roleId: '',
  brandId: '',
  isAdminFullAccess: false,
  isActive: true,
  companyIds: [],
};

export default function AdminUserForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id && id !== 'new';

  const [roles, setRoles] = useState<Role[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);

  useEffect(() => {
    loadRolesAndBrands();
    loadCompanies();
  }, []);

  useEffect(() => {
    if (!isEditing) return;
    api.get<{ success: boolean; data: User }>(`/admin/users/${id}`)
      .then((res) => {
        const u = res.data;
        setFormData({
          email: u.email,
          username: u.username ?? '',
          password: '',
          firstName: u.firstName,
          lastName: u.lastName,
          phone: u.phone || '',
          roleId: u.roleId,
          brandId: u.brandId || '',
          isAdminFullAccess: u.isAdminFullAccess,
          isActive: Boolean(u.isActive),
          companyIds: u.companyIds ?? [],
        });
      })
      .catch((err) => {
        console.error('[AdminUserForm] Error loading user:', err);
        setError("Error en carregar l'usuari");
      })
      .finally(() => setLoading(false));
  }, [id, isEditing]);

  const loadRolesAndBrands = async () => {
    try {
      const [rolesRes, brandsRes] = await Promise.all([
        api.get<{ success: boolean; data: Role[] }>('/admin/roles'),
        api.get<{ success: boolean; data: Brand[] }>('/admin/brands'),
      ]);
      setRoles(rolesRes.data || []);
      setBrands(brandsRes.data || []);
    } catch (err) {
      console.error('[AdminUserForm] Error loading roles/brands:', err);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await api.get<Company[]>('/companies');
      setCompanies(response || []);
    } catch (err) {
      console.error('[AdminUserForm] Error loading companies:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    if (!formData.isAdminFullAccess && formData.companyIds.length === 0) {
      setError("Cal seleccionar almenys una empresa");
      setSaving(false);
      return;
    }

    try {
      if (isEditing) {
        const payload: Record<string, unknown> = {
          username: formData.username.trim() || null,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone || undefined,
          roleId: formData.roleId,
          brandId: formData.brandId || undefined,
          isAdminFullAccess: formData.isAdminFullAccess,
          isActive: formData.isActive,
          companyIds: formData.companyIds,
        };
        if (formData.password) payload.password = formData.password;
        await api.put(`/admin/users/${id}`, payload);
      } else {
        await api.post('/admin/users', {
          email: formData.email,
          username: formData.username.trim() || undefined,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone || undefined,
          roleId: formData.roleId,
          brandId: formData.brandId || undefined,
          isAdminFullAccess: formData.isAdminFullAccess,
          isActive: formData.isActive,
          companyIds: formData.companyIds,
        });
      }
      navigate('/admin/users');
    } catch (err: any) {
      console.error('[AdminUserForm] Error saving user:', err);
      setError(err.response?.data?.message || "Error en desar l'usuari");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Capçalera sticky */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b flex items-center justify-between gap-4 py-3 -mx-4 px-4 md:-mx-6 md:px-6">
        <div className="flex items-center gap-3 min-w-0">
          <button type="button" onClick={() => navigate('/admin/users')}
            className="p-2 hover:bg-gray-100 rounded transition-colors flex-shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-foreground truncate">
              {isEditing ? `${formData.firstName} ${formData.lastName}`.trim() || 'Editar usuari' : 'Nou usuari'}
            </h1>
            {isEditing && formData.email && (
              <p className="text-xs text-muted-foreground">{formData.email}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button type="button" onClick={() => navigate('/admin/users')}
            className="px-4 py-2 border border-gray-200 rounded-lg text-xs text-gray-500 hover:bg-gray-50">
            Cancel·lar
          </button>
          <button type="submit" form="user-form" disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-primary/90 disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? 'Desant...' : 'Desar'}
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form id="user-form" onSubmit={handleSubmit} className="max-w-3xl space-y-6">
        <div className="bg-card border border-border rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-foreground">Dades d'accés</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Email</label>
              <input
                type="email"
                required
                disabled={isEditing}
                value={formData.email}
                onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg bg-background disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">
                Nom d'usuari <span className="text-muted-foreground font-normal">(per accedir)</span>
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData((p) => ({ ...p, username: e.target.value.toLowerCase().replace(/\s+/g, '') }))}
                placeholder="ex: jordi.pineda"
                className="w-full px-3 py-2 border rounded-lg bg-background font-mono focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">
              {isEditing ? 'Nova contrasenya (deixar en blanc per no canviar-la)' : 'Contrasenya'}
            </label>
            <input
              type="password"
              required={!isEditing}
              minLength={8}
              value={formData.password}
              onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-foreground">Dades personals</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Nom</label>
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={(e) => setFormData((p) => ({ ...p, firstName: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Cognoms</label>
              <input
                type="text"
                required
                value={formData.lastName}
                onChange={(e) => setFormData((p) => ({ ...p, lastName: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Telèfon</label>
            <input
              type="text"
              value={formData.phone}
              onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-foreground">Rol i marca</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Rol</label>
              <select
                required
                value={formData.roleId}
                onChange={(e) => setFormData((p) => ({ ...p, roleId: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">— Seleccionar —</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Marca</label>
              <select
                value={formData.brandId}
                onChange={(e) => setFormData((p) => ({ ...p, brandId: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">— Sense marca —</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-5 space-y-4">
          <h2 className="text-sm font-semibold text-foreground">Accés i permisos</h2>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData((p) => ({ ...p, isActive: e.target.checked }))}
              />
              Usuari actiu
            </label>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1">Empreses amb accés</label>
            {formData.isAdminFullAccess ? (
              <p className="text-xs text-muted-foreground italic">
                Té accés a totes les empreses (Accés total d'administrador).
              </p>
            ) : (
              <div className="border rounded-lg p-2 max-h-48 overflow-y-auto space-y-1">
                {companies.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">Carregant empreses...</p>
                ) : (
                  companies.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 text-xs cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={formData.companyIds.includes(c.id)}
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            companyIds: e.target.checked
                              ? [...p.companyIds, c.id]
                              : p.companyIds.filter((cid) => cid !== c.id),
                          }))
                        }
                      />
                      {c.name}
                    </label>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
